import { Inject, Injectable } from '@angular/core';
import { RestorationConfiguration } from '@shared/models/restoration-configuration-interface';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { distinctUntilChanged, map, shareReplay, withLatestFrom } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { RESTORATION_CONFIGURATION } from '@shared/rules/rules-tokens';
import { RestorationTypeForm } from '@modules/tooth-editor/models/restoration-type-from';
import { LoggerService } from '@core/services/logger/logger.service';
import { IdName } from '@shared/models/id-name';
import { Material } from '@shared/models/material';
import { getNewTooth, Tooth } from '@modules/teeth-diagram/models/tooth';
import type { Maybe } from '@/types/globals';

export type RestorationTypeIds = Pick<Tooth, 'ImplantBasedRestorationTypeId' | 'AbutmentType' | 'AbutmentMaterialId'>;

@Injectable()
export class RestorationTypeFacade {
	private readonly facadeName = 'RestorationTypeFacade';

	abutmentTypes$ = this.shellQuery.rxConfiguration$.pipe(
		map(rxConfiguration => rxConfiguration?.AbutmentTypes),
		map(abutmentTypes => abutmentTypes?.filter(x => this.configuration.DisplayedAbutmentTypeIds.includes(x.Id))),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	restorationTypes$ = this.shellQuery.restorationTypes$;

	constructor(
		@Inject(RESTORATION_CONFIGURATION) private configuration: RestorationConfiguration,
		private shellQuery: ShellQuery,
		private logger: LoggerService
	) {}

	getMaterials$(abutmentTypeId$: Observable<Maybe<number>>): Observable<IdName[]> {
		return abutmentTypeId$.pipe(
			distinctUntilChanged(),
			withLatestFrom(this.shellQuery.materials$),
			map(
				([abutmentTypeId, materials]) =>
					this.configuration.AvailableMaterials.find(x => abutmentTypeId && x.AbutmentTypeIds.includes(abutmentTypeId))
						?.MaterialIds.map(materialId => materials?.find(material => material.Id === materialId))
						.filter((x): x is Material => !!x) ?? []
			)
		);
	}

	getRestorationTypeForm$(ids$: Observable<RestorationTypeIds>): Observable<RestorationTypeForm> {
		return ids$.pipe(
			// eslint-disable-next-line @typescript-eslint/naming-convention
			map(({ ImplantBasedRestorationTypeId, AbutmentType, AbutmentMaterialId }) => {
				const emptyForm: RestorationTypeForm = {
					restorationType: null,
					abutmentType: null,
					abutmentMaterial: null,
					isTiBased: false
				};

				if (!ImplantBasedRestorationTypeId) {
					return emptyForm;
				}

				const abutmentTypeMap = this.configuration.AbutmentTypeMappings.find(x => x.TargetId === AbutmentType);

				if (AbutmentType && !abutmentTypeMap) {
					this.logger.error(`Didn't find abutmentTypeMapping with TargetId == ${AbutmentType}`, { module: this.facadeName });

					return emptyForm;
				}

				const foundRestorationType = this.shellQuery.restorationTypes?.find(x => x.Id === ImplantBasedRestorationTypeId) ?? null;
				const foundAbutmentType = abutmentTypeMap
					? this.shellQuery.abutmentTypes?.find(x => x.Id === abutmentTypeMap.VisibleId) ?? null
					: null;
				const foundMaterial = this.shellQuery.materials?.find(x => x.Id === AbutmentMaterialId) ?? null;

				return {
					restorationType: foundRestorationType,
					abutmentType: foundAbutmentType,
					abutmentMaterial: foundMaterial,
					isTiBased: abutmentTypeMap?.VisibleIsTiBase
				};
			})
		);
	}

	buildUpdatedIds(formValue: RestorationTypeForm, currentIds: RestorationTypeIds): RestorationTypeIds {
		const isTiBased = formValue.isTiBased ?? false;
		const abutmentTypeMap = this.configuration.AbutmentTypeMappings.find(
			x => x.VisibleId === formValue.abutmentType?.Id && x.VisibleIsTiBase === isTiBased
		);

		let abutmentMaterialId = formValue.abutmentMaterial?.Id ?? null;

		if (
			currentIds.AbutmentType !== abutmentTypeMap?.TargetId &&
			!this.configuration.AvailableMaterials.find(x =>
				x.AbutmentTypeIds.includes(abutmentTypeMap?.TargetId as any)
			)?.MaterialIds.includes(formValue.abutmentMaterial?.Id)
		) {
			abutmentMaterialId = null;
		}

		return {
			ImplantBasedRestorationTypeId: formValue.restorationType?.Id ?? getNewTooth().ImplantBasedRestorationTypeId,
			AbutmentType: abutmentTypeMap?.TargetId,
			AbutmentMaterialId: abutmentMaterialId
		};
	}
}
